import { PlatformIdentityService } from './identity/platform-identity.service';
import {inject, Injectable} from '@angular/core';
import {IClientOptions, ProductType} from '../../models/client-options.model';
import {BehaviorSubject, firstValueFrom, Subscription, timer} from 'rxjs';
import {HttpClient} from "@angular/common/http";
import {ClientSelectionDialogService} from '../layout/client-selection-dialog.service';
import {ClientStateService} from './client-state.service';
import {
    ClientWithProducts,
    ShineUserInfoService
} from './shine-auth/shine-user-info.service';
import {normalizeProductType, PRODUCT_ORDER} from '../../shared/products';
import {RIGHTS_PRODUCT_CATALOG} from '../../shared/rights-product-catalog';
import {Router} from '@angular/router';
import {productSlugFromType} from '../../shared/products';
import {ClientPreparationService} from '../objects/client-preparation.service';

interface ClientsCachePayload {
    schemaVersion: string;
    clients: IClientOptions[];
}

@Injectable({
    providedIn: 'root'
})
export class ClientService {
    private readonly router = inject(Router);
    private rightsPolling: Subscription | null = null;
    private http = inject(HttpClient);
    private identity = inject(PlatformIdentityService);
    private clientSelectionDialog = inject(ClientSelectionDialogService);
    private clientStateService = inject(ClientStateService);
    private shineUserInfoService = inject(ShineUserInfoService);
    private readonly preparation = inject(ClientPreparationService);

    private baseUrl = 'client/'; // Base URL for the client API
    private readonly ACTIVE_PRODUCT_COOKIE = 'activeProduct';
    private readonly CLIENT_ID_COOKIE = 'clientId';
    private readonly DEFAULT_ENABLED_PRODUCTS = ['Shine AI', 'Shine Enrich', 'Shine Connect', 'Shine Webinars', 'Shine Translations'];
    private readonly CLIENTS_CACHE_KEY = 'shine_clients_cache';
    private readonly CLIENTS_CACHE_TIMESTAMP_KEY = 'shine_clients_cache_timestamp';
    private readonly CLIENTS_CACHE_SCHEMA_VERSION = 'rights-v2-default-deny';
    private readonly UNKNOWN_CLIENT_NAMES = new Set(['unknown client', 'unknown company']);
    private readonly GRANTED_ACCESS_LABELS = new Set(['admin', 'user']);

    // Subject to emit product change events
    private activeProductSubject = new BehaviorSubject<string>('Shine AI');
    public activeProduct$ = this.activeProductSubject.asObservable();

    // Flag to track if a client refresh is in progress
    private isRefreshingClients = false;
    private hasStaleClientsCache = false;
    private hasLoadedFreshClients = false;
    private authoritativeAccountFingerprint: string | null = null;
    private identityGeneration = 0;

    public clients: IClientOptions[] = [];
    public client: IClientOptions | undefined;

    constructor() {
        // Set a flag to indicate this is a page reload
        // This will be used to avoid showing the client selection dialog on reload
        localStorage.setItem('shine_page_reloaded', 'true');

        // Initialize the active product subject with the current value from cookie
        const activeProduct = this.getCookie(this.ACTIVE_PRODUCT_COOKIE) || 'Shine AI';
        this.activeProductSubject.next(activeProduct);

        // Try to load clients from localStorage cache for faster initial load
        this.tryLoadClientsFromCache();
    }

    /**
     * Tries to load clients from localStorage cache for faster initial load
     * If successful, it will initialize the clients array and try to restore the selected client
     */
    private tryLoadClientsFromCache(): void {
        try {
            // Check if we have a valid cache
            const cacheTimestampStr = localStorage.getItem(this.CLIENTS_CACHE_TIMESTAMP_KEY);
            if (!cacheTimestampStr) {
                return;
            }

            const cacheTimestamp = parseInt(cacheTimestampStr, 10);
            const now = Date.now();
            const CACHE_DURATION_MS = 30 * 60 * 1000; // 30 minutes

            // If cache is too old, don't use it
            if (isNaN(cacheTimestamp) || (now - cacheTimestamp) > CACHE_DURATION_MS) {
                console.log('Clients cache is too old, not using it');
                this.invalidateClientsCache(true);
                return;
            }

            // Get clients from cache
            const clientsJson = localStorage.getItem(this.CLIENTS_CACHE_KEY);
            if (!clientsJson) {
                return;
            }

            const cachedClients = this.parseClientsCache(JSON.parse(clientsJson));
            if (!Array.isArray(cachedClients) || cachedClients.length === 0) {
                console.log('Invalid or empty clients cache');
                this.invalidateClientsCache(true);
                return;
            }

            if (cachedClients.some(client => this.isUnknownClient(client))) {
                console.log('Clients cache contains unknown clients, not using it');
                this.invalidateClientsCache(true);
                return;
            }

            console.log(`Loaded ${cachedClients.length} clients from localStorage cache`);
            this.clients = cachedClients;
            this.hasLoadedFreshClients = false;

            // Try to restore the selected client from cookie
            const savedClientId = this.getCookie(this.CLIENT_ID_COOKIE);
            if (savedClientId) {
                const savedClient = this.clients.find(c => c.id === savedClientId);
                if (savedClient) {
                    console.log('Restoring client from cookie');
                    this.client = savedClient;
                }
            }
        } catch (error) {
            console.error('Error loading clients from localStorage cache:', error);
            this.invalidateClientsCache(true);
        }
    }

    private parseClientsCache(value: unknown): IClientOptions[] | null {
        if (!value || typeof value !== 'object' || Array.isArray(value)) {
            return null;
        }

        const payload = value as Partial<ClientsCachePayload>;
        if (payload.schemaVersion !== this.CLIENTS_CACHE_SCHEMA_VERSION) {
            return null;
        }

        if (!Array.isArray(payload.clients)) {
            return null;
        }

        if (payload.clients.some(client => !client || !Array.isArray(client.enabledProducts))) {
            return null;
        }

        return payload.clients;
    }

    private invalidateClientsCache(markStale: boolean = false): void {
        localStorage.removeItem(this.CLIENTS_CACHE_KEY);
        localStorage.removeItem(this.CLIENTS_CACHE_TIMESTAMP_KEY);
        if (markStale) {
            this.hasStaleClientsCache = true;
            this.hasLoadedFreshClients = false;
            this.client = undefined;
            this.clients = [];
        }
    }

    /**
     * Removes client state that belongs to the previously active Entra identity.
     * This does not initiate a rights request; the caller establishes the next
     * identity before loading fresh rights.
     */
    public resetIdentityDerivedState(): void {
        this.preparation.reset();
        this.identityGeneration++;
        this.authoritativeAccountFingerprint = null;
        this.invalidateClientsCache(true);
        // A prior identity's request may now complete without emitting because its
        // rights generation is obsolete. Do not leave that request's guard active.
        this.isRefreshingClients = false;
        this.clearCookie(this.CLIENT_ID_COOKIE);
        this.clientStateService.triggerCacheReset();
    }

    /**
     * Sets a cookie with the specified name and value
     * @param name The name of the cookie
     * @param value The value to store in the cookie
     * @param days The number of days until the cookie expires (default: 30)
     */
    protected setCookie(name: string, value: string, days: number = 30): void {
        const date = new Date();
        date.setTime(date.getTime() + (days * 24 * 60 * 60 * 1000));
        const expires = "; expires=" + date.toUTCString();
        document.cookie = name + "=" + value + expires + "; path=/";
    }

    protected clearCookie(name: string): void {
        document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;`;
    }

    /**
     * Gets a cookie value by name
     * @param name The name of the cookie to retrieve
     * @returns The cookie value or null if not found
     */
    protected getCookie(name: string): string | null {
        const nameEQ = name + "=";
        const ca = document.cookie.split(';');
        for (let i = 0; i < ca.length; i++) {
            let c = ca[i];
            while (c.charAt(0) === ' ') c = c.substring(1, c.length);
            if (c.indexOf(nameEQ) === 0) return c.substring(nameEQ.length, c.length);
        }
        return null;
    }

    private mapRightsClient(clientWithProducts: any): IClientOptions | null {
        if (!this.hasValidRightsClient(clientWithProducts)) {
            return null;
        }
        const client = clientWithProducts?.client;
        if (!client) {
            return null;
        }

        const id = client.id;
        const name = client.Name || client.name || client.Domain || client.domain || id;

        if (!id || this.UNKNOWN_CLIENT_NAMES.has(String(name).trim().toLowerCase())) {
            return null;
        }

        const enabledProducts = this.getGrantedProductNames(clientWithProducts);

        return {
            id,
            name,
            logo: client.Logo || client.logo || '',
            description: client.Description || client.description || '',
            domain: client.Domain || client.domain || '',
            enabledProducts
        };
    }

    private hasValidRightsClient(value: unknown): boolean {
        if (!value || typeof value !== 'object' || Array.isArray(value)) return false;
        const rights = value as {client?: unknown; enabledProducts?: unknown; moduleAccess?: unknown};
        if (!rights.client || typeof rights.client !== 'object' || Array.isArray(rights.client)
            || !Object.prototype.hasOwnProperty.call(rights.client, 'id')) return false;
        const id = (rights.client as {id?: unknown}).id;
        if (typeof id !== 'string' || !id.trim()) return false;
        if (!Array.isArray(rights.enabledProducts)
            || (rights.moduleAccess !== undefined && !Array.isArray(rights.moduleAccess))) return false;

        const accesses = new Map<string, string>();
        const supportedAccesses = new Set(['admin', 'user', 'read-only', 'billing']);
        return [rights.enabledProducts, rights.moduleAccess ?? []].every(source => {
            const names = new Set<string>();
            return Array.from(source as unknown[]).every(entry => {
                if (!entry || typeof entry !== 'object' || Array.isArray(entry)) return false;
                const product = entry as {name?: unknown; access?: unknown};
                if (typeof product.name !== 'string' || typeof product.access !== 'string') return false;
                const name = product.name.trim();
                const access = product.access.trim().toLowerCase();
                if (!RIGHTS_PRODUCT_CATALOG.includes(name) || !supportedAccesses.has(access) || names.has(name)
                    || (accesses.has(name) && accesses.get(name) !== access)) return false;
                names.add(name);
                accesses.set(name, access);
                return true;
            });
        });
    }

    private hasValidRightsList(value: unknown): value is ClientWithProducts[] {
        return Array.isArray(value) && Array.from(value).every(client => this.hasValidRightsClient(client))
            && new Set(value.map(client => client.client.id)).size === value.length;
    }

    private invalidateAuthoritativeRights(): void {
        this.invalidateClientsCache(true);
        this.authoritativeAccountFingerprint = null;
        this.preparation.reset();
    }

    private isUnknownClient(client: IClientOptions): boolean {
        return this.UNKNOWN_CLIENT_NAMES.has((client.name || '').trim().toLowerCase());
    }

    private getGrantedProductNames(clientWithProducts: any): string[] {
        const grantedNames = new Set<string>();
        const sources = [
            ...(Array.isArray(clientWithProducts?.enabledProducts) ? clientWithProducts.enabledProducts : []),
            ...(Array.isArray(clientWithProducts?.moduleAccess) ? clientWithProducts.moduleAccess : []),
        ];

        sources.forEach((product: any) => {
            const name = typeof product?.name === 'string' ? product.name.trim() : '';
            const access = typeof product?.access === 'string' ? product.access.trim().toLowerCase() : '';

            if (this.isKnownProduct(name) && this.GRANTED_ACCESS_LABELS.has(access)) {
                grantedNames.add(name);
            }
        });

        return Array.from(grantedNames);
    }

    private hasExplicitProductGrant(client: IClientOptions): boolean {
        return client.enabledProducts.length > 0;
    }

    private matchesClientIdentifier(client: IClientOptions, id: string): boolean {
        return client.id === id || client.domain === id;
    }

    private findClientByIdentifier(clients: IClientOptions[], id: string): IClientOptions | undefined {
        return clients.find(client => this.matchesClientIdentifier(client, id));
    }

    private isIdentityCurrent(identityGeneration: number, accountFingerprint: string): boolean {
        return identityGeneration === this.identityGeneration
            && accountFingerprint === this.identity.fingerprint;
    }

    private chooseClientForSelection(clients: IClientOptions[], savedClientId?: string | null): IClientOptions | undefined {
        const savedClient = savedClientId ? this.findClientByIdentifier(clients, savedClientId) : undefined;
        const firstGrantedClient = clients.find(client => this.hasExplicitProductGrant(client));

        if (savedClient && (this.hasPlatformProductAccess(savedClient)
            || this.hasExplicitProductGrant(savedClient) || !firstGrantedClient)) {
            return savedClient;
        }

        return firstGrantedClient ?? savedClient ?? clients[0];
    }

    private mergeClients(clients: IClientOptions[]): void {
        clients.forEach(client => {
            const existingClientIndex = this.clients.findIndex(existingClient => existingClient.id === client.id);
            if (existingClientIndex >= 0) {
                this.clients[existingClientIndex] = client;
            } else {
                this.clients.push(client);
            }
        });
    }

    /**
     * Applies an already-authoritative rights response without requesting it again.
     * Account bootstrap uses this path so client selection cannot turn one rights
     * response into a second request while the client cache is empty.
     */
    public async applyAuthoritativeRights(
        clientWithProducts: ClientWithProducts[],
        preferredClientId?: string,
        preserveSelection: boolean = false,
    ): Promise<void> {
        const identityGeneration = this.identityGeneration;
        const accountFingerprint = this.identity.fingerprint;
        if (!accountFingerprint) {
            this.resetIdentityDerivedState();
            return;
        }

        if (!this.hasValidRightsList(clientWithProducts)) {
            // Do not drop a malformed selected row and silently choose another client.
            this.invalidateAuthoritativeRights();
            return;
        }

        const clients = clientWithProducts
            .map(client => this.mapRightsClient(client))
            .filter((client): client is IClientOptions => client !== null);

        if (!this.isIdentityCurrent(identityGeneration, accountFingerprint)) {
            return;
        }

        this.clients = clients;
        this.hasLoadedFreshClients = true;
        this.hasStaleClientsCache = false;
        this.authoritativeAccountFingerprint = accountFingerprint;

        try {
            const payload: ClientsCachePayload = {
                schemaVersion: this.CLIENTS_CACHE_SCHEMA_VERSION,
                clients,
            };
            localStorage.setItem(this.CLIENTS_CACHE_KEY, JSON.stringify(payload));
            localStorage.setItem(this.CLIENTS_CACHE_TIMESTAMP_KEY, Date.now().toString());
            console.log('Stored clients in localStorage cache');
        } catch (error) {
            console.error('Error storing clients in localStorage:', error);
        }

        const savedClientId = this.getCookie(this.CLIENT_ID_COOKIE);
        const currentClientId = this.client?.id;
        const selectionId = currentClientId ?? savedClientId;
        const selectedClient = preserveSelection && selectionId
            ? this.findClientByIdentifier(this.clients, selectionId)
            : this.chooseClientForSelection(this.clients, preferredClientId ?? savedClientId ?? currentClientId);

        if (selectedClient) {
            if (preserveSelection && selectionId) {
                this.client = selectedClient;
            } else {
                await this.setClientAsync(selectedClient.id);
            }
        } else {
            this.client = undefined;
            if (preserveSelection && selectionId) {
                // Retain a removed selection so later polls cannot silently switch tenants.
                this.setCookie(this.CLIENT_ID_COOKIE, selectionId);
            } else {
                this.clearCookie(this.CLIENT_ID_COOKIE);
            }
        }

        if (!this.isIdentityCurrent(identityGeneration, accountFingerprint)) {
            if (identityGeneration === this.identityGeneration) {
                this.resetIdentityDerivedState();
            }
            return;
        }

        this.startRightsPolling();
        if (this.clients.length > 1) {
            localStorage.removeItem('shine_page_reloaded');
        }
    }

    /**
     * Retrieves a client by ID and stores it in cookies.
     * @param id The ID of the client to retrieve.
     * @returns A Promise that resolves when the client info has been fetched.
     */
    async setClientAsync(id: string): Promise<void> {
        // Check if this is a different client than the current one
        const isClientChange = !this.client || this.client.id !== id;

        // First try to find the client in the local clients array
        this.client = this.findClientByIdentifier(this.clients, id);

        // If the client is not found locally, fetch it using getUserRights.
        // A normal client switch should use the already-loaded rights payload instead of
        // forcing another API call during login/bootstrap.
        if (!this.client) {
            const identityGeneration = this.identityGeneration;
            const accountFingerprint = this.identity.fingerprint;
            if (!accountFingerprint) return;
            try {
                console.log('Client not found in local cache or refresh needed, fetching from getUserRights');

                // Use getUserRights to get client information
                const response = await firstValueFrom(this.shineUserInfoService.getUserRights(true));
                if (!this.isIdentityCurrent(identityGeneration, accountFingerprint)) return;

                if (response?.status === 'success' && response.data && response.data.clients) {
                    if (!this.hasValidRightsList(response.data.clients)) {
                        this.invalidateAuthoritativeRights();
                        return;
                    }
                    const mappedClients = response.data.clients
                        .map(clientWithProducts => this.mapRightsClient(clientWithProducts))
                        .filter((client): client is IClientOptions => client !== null);

                    this.mergeClients(mappedClients);

                    const clientInfo = this.findClientByIdentifier(mappedClients, id);

                    if (clientInfo) {
                        // Set the client
                        this.client = clientInfo;
                    } else {
                        console.warn('Client not found in getUserRights response');
                    }
                }
            } catch (error) {
                if (!this.isIdentityCurrent(identityGeneration, accountFingerprint)) return;
                console.error('Error fetching client info from getUserRights:', error);

                // If we couldn't fetch the client info, use the local client if available
                if (!this.client) {
                    console.warn('Could not fetch client info and no local client found');
                    return;
                }
            }
        }

        if (this.client) {
            // Store the client ID in cookies
            this.setCookie(this.CLIENT_ID_COOKIE, this.client.id);

            // Ensure the active product is valid for the new client
            const currentProduct = this.getActiveProduct();
            if (!this.canAccessProduct(currentProduct)) {
                const firstProduct = this.client.enabledProducts[0] || 'Shine AI';

                const normalized = normalizeProductType(firstProduct);
                this.setActiveProduct(normalized);
            }

            // If the client has changed, trigger a cache reset
            if (isClientChange) {
                this.clientStateService.triggerCacheReset();
            }
            // Cached rights can paint the shell, but cannot trigger configuration writes.
            if (this.hasLoadedFreshClients) {
                this.preparation.select(this.client.id,
                    this.client.enabledProducts.some(product => normalizeProductType(product) === 'Shine AI'));
            }
        } else {
            console.warn('Client not found');
        }
    }

    /**
     * Retrieves the list of clients.
     * @param force Request a fresh refresh; concurrent callers still share the current request
     */
    refreshClientsAsync(force: boolean = false, preserveSelection: boolean = false): void {
        // Never overlap refreshes, including forced callers.
        if (this.isRefreshingClients) {
            return;
        }

        // If force is true, reset the flag
        if (force) {
            console.log('Forcing client refresh');
            this.isRefreshingClients = false;
        }

        try {
            this.isRefreshingClients = true;
            console.log('Refreshing clients using getUserRights endpoint');
            const identityGeneration = this.identityGeneration;
            const accountFingerprint = this.identity.fingerprint;
            if (!accountFingerprint) {
                this.resetIdentityDerivedState();
                return;
            }

            // Store the current URL in localStorage to restore it after reload if needed
            if (window.location.pathname !== '/' && window.location.pathname !== '/dashboard') {
                localStorage.setItem('shine_last_path', window.location.pathname + window.location.search);
                console.log('Stored current path in localStorage');
            }

            // Use the getUserRights endpoint to get client information and enabled products in a single call
            this.shineUserInfoService.getUserRights(true).subscribe({
                next: async (response) => {
                    if (!this.isIdentityCurrent(identityGeneration, accountFingerprint)) {
                        if (identityGeneration === this.identityGeneration) {
                            this.isRefreshingClients = false;
                        }
                        return;
                    }

                    if (response?.status !== 'success' || !response.data || !response.data.clients) {
                        console.warn('Failed to get user rights or no clients found');
                        this.hasLoadedFreshClients = false;
                        this.isRefreshingClients = false;
                        return;
                    }

                    await this.applyAuthoritativeRights(response.data.clients, undefined, preserveSelection);

                    if (!this.isIdentityCurrent(identityGeneration, accountFingerprint)) {
                        return;
                    }

                    if (preserveSelection) this.reconcileProductRoute();
                    console.log('Client refresh completed successfully');
                    this.isRefreshingClients = false;
                },
                error: (err) => {
                    if (!this.isIdentityCurrent(identityGeneration, accountFingerprint)) {
                        return;
                    }

                    console.error('Failed to refresh clients', err);
                    this.hasLoadedFreshClients = false;
                    this.isRefreshingClients = false;
                }
            });
        } catch (error) {
            console.error('Error creating URL for client refresh:', error);
            this.isRefreshingClients = false;
        }
    }

    private startRightsPolling(): void {
        if (this.rightsPolling) return;
        this.rightsPolling = timer(30_000, 30_000).subscribe(() => {
            if (document.visibilityState === 'hidden'
                || !this.identity.fingerprint) return;
            this.refreshClientsAsync(false, true);
        });
    }

    private reconcileProductRoute(): void {
        const product = this.getActiveProduct();
        const slug = productSlugFromType(product);
        const url = this.router.url;
        const landing = `/products/${slug}/landing`;
        if (url.startsWith(landing) && this.canAccessProduct(product)) {
            const from = this.router.parseUrl(url).queryParams['from'];
            // Only restore a denied route inside the same product.
            if (typeof from === 'string' && from.startsWith(`/${slug}/`)) {
                void this.router.navigateByUrl(from);
            }
        } else if ((url === `/${slug}` || url.startsWith(`/${slug}/`)) && !this.canAccessProduct(product)) {
            void this.router.navigate(['/products', slug, 'landing'], {queryParams: {from: url}});
        }
    }

    ngOnDestroy(): void {
        this.rightsPolling?.unsubscribe();
    }

    /**
     * Handles client selection based on the number of available clients
     * - If there's only one client, select it automatically
     * - If there are multiple clients, use a previously used entitled client or first entitled client
     */
    public async handleClientSelection(): Promise<void> {
        if (this.clients.length === 1) {
            // If there's only one client, select it automatically
            await this.setClientAsync(this.clients[0].id);
        } else if (this.clients.length > 1) {
            // If there are multiple clients, check for a previously used entitled client or use the first entitled one.
            const savedClientId = this.getCookie(this.CLIENT_ID_COOKIE);
            
            if (savedClientId) {
                // Check if the saved client ID exists in the available clients
                const savedClient = this.chooseClientForSelection(this.clients, savedClientId);
                
                if (savedClient) {
                    console.log('Using eligible client selection');
                    await this.setClientAsync(savedClient.id);
                } else {
                    console.log('Previously selected client not found, using first entitled client');
                    await this.setClientAsync(this.chooseClientForSelection(this.clients)?.id ?? this.clients[0].id);
                }
            } else {
                console.log('No previously selected client, using first entitled client');
                await this.setClientAsync(this.chooseClientForSelection(this.clients)?.id ?? this.clients[0].id);
            }
        }
    }

    // The following methods have been removed as client editing is now disabled:
    // - createClientAsync
    // - saveClientAsync
    // - publishToEnvironment
    // - writeDescriptionForClientAsync

    // These operations are now managed through shine-auth

    // The following methods have been removed as API key management is now disabled:
    // - removeKey
    // - addKey
    // - generateUUID
    // - generateApiKey

    // These operations are now managed through shine-auth

    createNewClient() : IClientOptions {
        return  {
            id: '',
            name: '',
            logo: '',
            description: '',
            domain: '',
            enabledProducts: ['Shine AI', 'Shine Enrich', 'Shine Connect', 'Shine Webinars', 'Shine Translations'], // Enable products by default
        }
    }

    /**
     * Gets the currently active product for the selected client
     * @returns The active product type as a string
     */
    getActiveProduct(): string {
        // Get the active product from cookie
        const activeProduct = this.getCookie(this.ACTIVE_PRODUCT_COOKIE) || 'Shine AI';
        // If client is undefined, log a warning but don't trigger refresh
        // Client refresh should only be triggered after authentication is complete
        if (!this.client && this.clients.length === 0) {
            console.log('No client selected and no clients loaded, using default product');
        }

        return activeProduct;
    }

    /**
     * Sets the active product for the current session and stores it in cookies
     * @param product The product to set as active
     */
    setActiveProduct(product: string): void {
        this.setCookie(this.ACTIVE_PRODUCT_COOKIE, product);
        // Emit the product change event
        this.activeProductSubject.next(product);
    }

    /**
     * Determines if the selected client has access to the given product
     */
    hasProduct(product?: string | null): boolean {
        if (!product || product === 'all') {
            return true;
        }

        const normalizedTarget = normalizeProductType(product);
        const enabledProducts = this.client?.enabledProducts ?? [];

        // Product routes are authorization boundaries. Browser caches may seed display
        // state, but route access must wait for a fresh rights response.
        if (!this.client || !this.hasFreshClientRights()) {
            return false;
        }

        if (enabledProducts.length === 0) {
            return false;
        }

        return enabledProducts.some(enabled => normalizeProductType(enabled) === normalizedTarget);
    }

    /** UI navigation access; hasProduct continues to describe actual subscriptions. */
    canAccessProduct(product: unknown): boolean {
        return this.isKnownProduct(product) && this.hasProductAccessContext()
            && (this.identity.isPlatformAdmin === true || this.hasProduct(product));
    }

    /** Invalid or stale selections must cancel navigation rather than seed route state. */
    hasProductAccessContext(): boolean {
        return this.identity.authenticated === true && this.hasFreshClientRights()
            && this.isValidProductAccessClient(this.client);
    }

    private hasPlatformProductAccess(client: IClientOptions | undefined): boolean {
        return this.identity.authenticated === true && this.identity.isPlatformAdmin === true
            && this.hasFreshClientRights() && this.isValidProductAccessClient(client);
    }

    private isKnownProduct(product: unknown): product is ProductType {
        return typeof product === 'string' && PRODUCT_ORDER.includes(product as ProductType);
    }

    private isValidProductAccessClient(client: IClientOptions | undefined): boolean {
        if (!client || typeof client !== 'object' || Array.isArray(client)
            || typeof client.id !== 'string' || !client.id.trim()
            || !Array.isArray(client.enabledProducts)
            || !Array.from(client.enabledProducts).every(product => this.isKnownProduct(product))
            || new Set(client.enabledProducts).size !== client.enabledProducts.length
            || !Array.isArray(this.clients)) {
            return false;
        }

        const matches = this.clients.filter(candidate => candidate?.id === client.id);
        return matches.length === 1 && matches[0] === client;
    }

    /**
     * True only after this service has loaded client rights from the server in
     * the current browser session. localStorage clients are bootstrap hints.
     */
    hasFreshClientRights(): boolean {
        const activeAccountFingerprint = this.identity.fingerprint;
        return this.hasLoadedFreshClients
            && activeAccountFingerprint !== null
            && activeAccountFingerprint === this.authoritativeAccountFingerprint;
    }

    /**
     * Forces a refresh of client data, resetting all caches
     * This should be used when you need to ensure fresh client data
     */
    forceRefreshClients(): void {
        console.log('Forcing refresh of client data');

        // Trigger a cache reset through the state service
        console.log('Triggering cache reset');
        this.clientStateService.triggerCacheReset();

        // Call refreshClientsAsync with force=true
        console.log('Calling refreshClientsAsync with force=true');
        this.refreshClientsAsync(true);
    }
}
